/**
 * <TutorApplicationJourneySkeleton> — la forme du parcours de la candidature pendant le premier
 * chargement (le squelette de `<TutorApplicationPage>`).
 *
 * « Étape n sur 6 » et sa barre, puis le chemin
 * d'un profil d'un seul domaine — deux étapes faites, l'étape du moment ouverte dans sa carte encadrée
 * (son titre, sa phrase, le champion et sa bulle, le bouton), et les trois à venir. Les géométries sont
 * celles du parcours (`JOURNEY_NODE_BOX`, `JOURNEY_FIGURE_NARROW` et la toile de `<ChampionFigure>`),
 * de la barre (`STEP_BAR_HEIGHT`) et du bouton (`pushButtonBoxHeight`).
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { pushButtonBoxHeight } from "../../atoms/Buttons/Button/Button"
import { FIGURE_RATIO } from "../../atoms/ChampionFigure/championDance"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { STEP_BAR_HEIGHT } from "../../atoms/SegmentedBar"
import { Skeleton } from "../../atoms/Skeleton"
import { Card } from "../../molecules/Card"
import { HighlightFrame } from "../../molecules/HighlightFrame"
import { JOURNEY_FIGURE_NARROW, JOURNEY_NODE_BOX } from "./TutorApplicationJourney"

/** Le cas courant : un profil d'un seul domaine, à l'étape 3. */
const STEPS_BEFORE = 2
const STEPS_AFTER = 3

export function TutorApplicationJourneySkeleton() {
    return (
        <Stack gap="5">
            <Stack gap="2">
                <Row justify="between" align="end">
                    <Skeleton textSize="text-lg" width={120} />
                    <Skeleton textSize="text-sm" width={72} />
                </Row>
                <Skeleton shape="block" height={STEP_BAR_HEIGHT} radius="full" />
            </Stack>
            <Stack gap="4">
                {Array.from({ length: STEPS_BEFORE }, (_, index) => (
                    <StopSkeleton key={`before-${index}`} />
                ))}
                <Row gap="3" align="start">
                    <Skeleton shape="circle" size={JOURNEY_NODE_BOX} />
                    <Stack style={styles.card}>
                        <HighlightFrame>
                            <Card p="5">
                                <Stack gap="4">
                                    <Skeleton textSize="text-sm" width={140} />
                                    <Stack gap="1">
                                        <Skeleton textSize="text-xl" width="70%" />
                                        <Skeleton textSize="text-base" width="90%" />
                                    </Stack>
                                    <Row gap="2" align="end">
                                        <Stack style={styles.card}>
                                            <Skeleton shape="block" height={JOURNEY_FIGURE_NARROW / 2} radius="lg" />
                                        </Stack>
                                        <Skeleton
                                            shape="block"
                                            width={Math.round(JOURNEY_FIGURE_NARROW * FIGURE_RATIO)}
                                            height={JOURNEY_FIGURE_NARROW}
                                            radius="lg"
                                        />
                                    </Row>
                                    <Skeleton shape="block" height={pushButtonBoxHeight()} radius="md" />
                                </Stack>
                            </Card>
                        </HighlightFrame>
                    </Stack>
                </Row>
                {Array.from({ length: STEPS_AFTER }, (_, index) => (
                    <StopSkeleton key={`after-${index}`} />
                ))}
            </Stack>
        </Stack>
    )
}

function StopSkeleton() {
    return (
        <Row gap="3" align="center">
            <Skeleton shape="circle" size={JOURNEY_NODE_BOX} />
            <Stack gap="1">
                <Skeleton textSize="text-md" width={160} />
                <Skeleton textSize="text-sm" width={96} />
            </Stack>
        </Row>
    )
}

const styles = StyleSheet.create({ card: { flex: 1, minWidth: 0 } })
